/**
 * Docking.
 *
 * A berth is claimed by flying into the space above it, not by pressing a
 * button from anywhere in the system. That means there has to be an actual
 * volume, and the volume has to be somewhere you can see — so this file answers
 * two questions with the same numbers: where is the box, and are we in it.
 *
 * No vectors library here on purpose. The renderer owns three.js; this owns the
 * rule, so it can be tested without a scene.
 */
import { LINE_HEALTH } from './upgrades.js';
import { SIZE_CLASSES, sizeRank } from '../world/types.js';
/**
 * A hull's extent, from the box its geometry occupies.
 *
 * Both numbers are a *reach from the ship's origin*, not half a span — and the
 * difference is not academic, because a hull is not centred on its origin. The
 * starter runs from -1.5 to +7.5 along its length: half the span is 4.5, but the
 * nose is 7.5 out. Measuring it as half a span left three units of ship with no
 * collision on it and no clearance check, so the front could be flown through a
 * station deck while the middle bounced off it.
 *
 * Taking the reach is conservative for a hull that *is* centred and correct for
 * one that is not, which is the right way round: the cost of being generous is a
 * berth that asks for slightly more room than it strictly needs.
 *
 * The floor exists because a hull with no geometry to measure — a mesh whose
 * parts have not finished loading — must not report as a point.
 */
export function shipExtentOf(bounds, floor) {
    const reach = (low, high) => Math.max(Math.abs(low), Math.abs(high));
    return {
        plan: Math.max(floor, reach(bounds.min.x, bounds.max.x), reach(bounds.min.y, bounds.max.y)),
        depth: Math.max(floor, reach(bounds.min.z, bounds.max.z)),
    };
}
/**
 * How tall the volume is relative to its footprint.
 *
 * Squat, and deliberately more so than it started. Approach is a horizontal
 * problem — lining up over the right plate — and a tall column let you sit high
 * enough above a station to be clear of the whole structure and still be told
 * you were docked. Two thirds of the original height means you have to come
 * down onto the pad rather than hover over it.
 */
const HEIGHT_RATIO = 0.53;
/** How much wider than the plate the volume is. */
export const DOCK_TOLERANCE = 1.1;
/**
 * How far the volume's floor is sunk into the plate, as a fraction of its own
 * half-height.
 *
 * The box used to sit exactly flush on the landing surface, which is right
 * arithmetically and wrong on screen: two coplanar faces at station range are a
 * depth-buffer coin toss, and the hologram's floor flickered against the top of
 * the plate. Sinking it a little puts the floor unambiguously *inside* the
 * plate, where it cannot be seen to fight with anything.
 *
 * Small enough not to be a rules change. It lowers the ceiling by the same
 * amount — a few tenths of a unit on a fourteen-unit box — which is nothing
 * against a landing you have to fly down into anyway.
 */
const DOCK_SINK = 0.04;
/**
 * How far out the volume makes itself visible, as a multiple of its own size.
 *
 * Capture and display are deliberately different distances: the box is only a
 * tenth wider than the plate, so a hologram that waited for contact would blink
 * into existence at the moment it stopped being any use. Three times out is far
 * enough to see the berth, turn, and fly down into it.
 */
export const DOCK_SHOW_RANGE = 3;
export function dockVolume(padExtent) {
    const extent = padExtent * DOCK_TOLERANCE;
    const halfHeight = (extent * HEIGHT_RATIO) / 2;
    return {
        half: { x: extent / 2, y: extent / 2, z: halfHeight },
        // Not `halfHeight` exactly: see `DOCK_SINK`. Flush was a z-fight.
        centreHeight: halfHeight * (1 - DOCK_SINK),
    };
}
/**
 * What it costs to take a berth bigger than the ship needs.
 *
 * Ten linelits per size class above the smallest that would have fitted, so a
 * medium pad for a small hull is 10 and a massive one is 30.
 *
 * ── Why a fee rather than a refusal ─────────────────────────────────────────
 *
 * Docking anywhere that fits should always be *possible*: a station may have
 * nothing small free, and a rule that turned a bad berth into no berth would
 * strand somebody over a plate they could see. But a big pad is a scarce thing
 * a bigger ship needs, and taking one for a shuttle should cost something. A
 * fee is a nudge you can always overrule when you are in a hurry, which is
 * exactly the strength this wants.
 */
export const OVERSIZE_FEE_PER_CLASS = 10;
/**
 * The fee for putting this hull on this pad.
 *
 * Zero when the pad is the smallest class the ship would fit on, which is the
 * common case and the one nobody should be charged for.
 */
export function dockingFee(padSize, smallestThatFits) {
    const over = sizeRank(padSize) - sizeRank(smallestThatFits);
    return Math.max(0, over) * OVERSIZE_FEE_PER_CLASS;
}
/**
 * The smallest class of pad this hull could use.
 *
 * Walked from the smallest upward against the same `fitsBerth` the capture
 * volume uses, so "would have fitted" means exactly what docking means by it
 * rather than a second opinion about hull size.
 */
export function smallestBerthFor(ship, padExtentOf) {
    for (const size of SIZE_CLASSES) {
        if (fitsBerth(dockVolume(padExtentOf(size)), ship))
            return size;
    }
    return SIZE_CLASSES[SIZE_CLASSES.length - 1];
}
/** Whether a hull is small enough to be captured by this berth at all. */
export function fitsBerth(volume, ship) {
    return (ship.plan <= volume.half.x &&
        ship.plan <= volume.half.y &&
        ship.depth <= volume.half.z);
}
/** Where the ship stands, given its offset from the volume's centre. */
export function dockApproach(offset, volume, ship) {
    const axes = [
        ['x', ship.plan],
        ['y', ship.plan],
        ['z', ship.depth],
    ];
    let inside = true;
    for (const [axis, half] of axes) {
        const reach = Math.abs(offset[axis]);
        // Out of sight of it on one axis is enough to be clear on all of them.
        if (reach - half >= volume.half[axis] * DOCK_SHOW_RANGE)
            return 'clear';
        if (reach + half > volume.half[axis])
            inside = false;
    }
    return inside ? 'captured' : 'entering';
}
/**
 * Setting down without any gear to put down.
 *
 * The gear-down requirement is a check that you have *lowered* it, and a ship
 * that has lost every leg cannot satisfy it however carefully it flies — so
 * without an exemption a hull that took a bad hit is locked out of every berth
 * in the galaxy and can only be flown until something finishes it. That is a
 * dead end rather than a difficulty.
 *
 * It has to be *all* of them. One leg left is a ship that can still land badly,
 * and letting it skip the requirement would make gear something you tear off on
 * purpose.
 */
export function canBellyLand(componentTypes) {
    return !componentTypes.includes('landingGear');
}
export function setDown(componentTypes, gearDown) {
    // No legs at all is the exemption. One leg left is a ship that can still land
    // badly, and letting it skip the requirement would make gear something to
    // tear off on purpose -- see `canBellyLand`.
    if (canBellyLand(componentTypes))
        return 'belly';
    return gearDown ? 'gear' : 'raised';
}
/** Whether a ship in this state may set down at all. */
export function mayDock(how) {
    return how !== 'raised';
}
/** What a landing on the hull costs, as a fraction of every line. */
export const BELLY_LANDING_TOLL = 0.05;
/**
 * Put a hull down on its belly.
 *
 * Five per cent off every line, and never fatal — a line is left with a point
 * rather than taken to zero. A landing that could destroy the ship is not an
 * emergency option, it is a coin flip, and the whole reason this exists is to
 * give a wrecked hull somewhere to go.
 *
 * Flat rather than divided by strength, because it is not an impact — it is the
 * whole ship taking its own weight on parts not meant to carry it.
 */
export function bellyLand(hull) {
    return {
        ...hull,
        lines: hull.lines.map((line) => {
            if (line.health <= 0)
                return line;
            return { ...line, health: Math.max(1, line.health - LINE_HEALTH * BELLY_LANDING_TOLL) };
        }),
    };
}
//# sourceMappingURL=docking.js.map